/**
 * Bitwarden/Vaultwarden client-side encryption utilities
 * Uses Web Crypto API for all cryptographic operations
 *
 * Encryption flow:
 * 1. Master Password + Email (salt) → PBKDF2-SHA256 → Source Key
 * 2. Source Key → HKDF-Expand → Encryption Key + MAC Key
 * 3. CipherString: Type.IV|Ciphertext|MAC → AES-256-CBC decrypt
 *
 * Reference: https://github.com/bitwarden/clients/blob/main/libs/common/src/platform/services/crypto.service.ts
 */

import { CipherType, FieldType, type VaultCipher } from '@/api/vault';

// Encryption types (from Bitwarden)
export enum EncryptionType {
  AesCbc256_B64 = 0,
  AesCbc128_HmacSha256_B64 = 1,
  AesCbc256_HmacSha256_B64 = 2, // Most common
  Rsa2048_OaepSha256_B64 = 3,
  Rsa2048_OaepSha1_B64 = 4,
  Rsa2048_OaepSha256_HmacSha256_B64 = 5,
  Rsa2048_OaepSha1_HmacSha256_B64 = 6,
}

export interface DecryptedKeys {
  encKey: CryptoKey;
  macKey: CryptoKey;
}

export interface SymmetricCryptoKey {
  key: ArrayBuffer; // Full 64-byte key (32 enc + 32 mac)
  encKey: CryptoKey;
  macKey: CryptoKey;
}

// Parse CipherString format: Type.IV|CT|MAC or Type.IV|CT
interface ParsedCipherString {
  encType: EncryptionType;
  iv: Uint8Array;
  ct: Uint8Array;
  mac?: Uint8Array;
}

/**
 * Convert string to UTF-8 ArrayBuffer
 */
function stringToArrayBuffer(str: string): ArrayBuffer {
  return new TextEncoder().encode(str).buffer;
}

/**
 * Convert ArrayBuffer to UTF-8 string
 */
function arrayBufferToString(buffer: ArrayBuffer): string {
  return new TextDecoder().decode(buffer);
}

/**
 * Base64 decode to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert Uint8Array to ArrayBuffer (not SharedArrayBuffer)
 */
export function toArrayBuffer(arr: Uint8Array): ArrayBuffer {
  return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer;
}

/**
 * ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * HKDF-Expand (RFC 5869)
 * Expands key material using HKDF with SHA-256
 */
async function hkdfExpand(
  prk: ArrayBuffer,
  info: string,
  outputLength: number,
): Promise<ArrayBuffer> {
  const hashLen = 32; // SHA-256 output length
  const iterations = Math.ceil(outputLength / hashLen);

  const infoBytes = new TextEncoder().encode(info);
  let okm = new Uint8Array(0);
  let previousT = new Uint8Array(0);

  const key = await crypto.subtle.importKey('raw', prk, { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);

  for (let i = 1; i <= iterations; i++) {
    const data = new Uint8Array(previousT.length + infoBytes.length + 1);
    data.set(previousT);
    data.set(infoBytes, previousT.length);
    data[previousT.length + infoBytes.length] = i;

    const signature = await crypto.subtle.sign('HMAC', key, data);
    previousT = new Uint8Array(signature);

    const newOkm = new Uint8Array(okm.length + previousT.length);
    newOkm.set(okm);
    newOkm.set(previousT, okm.length);
    okm = newOkm;
  }

  return okm.slice(0, outputLength).buffer;
}

/**
 * Derive master key from password using PBKDF2
 * Returns 32-byte key
 */
export async function deriveMasterKey(
  password: string,
  email: string,
  kdfIterations: number,
): Promise<ArrayBuffer> {
  const passwordBuffer = stringToArrayBuffer(password);
  const salt = stringToArrayBuffer(email.toLowerCase());

  const baseKey = await crypto.subtle.importKey('raw', passwordBuffer, 'PBKDF2', false, [
    'deriveBits',
  ]);

  const masterKey = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: kdfIterations,
      hash: 'SHA-256',
    },
    baseKey,
    256, // 32 bytes
  );

  return masterKey;
}

/**
 * Compute master password hash for login
 * masterPasswordHash = PBKDF2(masterKey, password, 1)
 */
export async function computeMasterPasswordHash(
  masterKey: ArrayBuffer,
  password: string,
): Promise<string> {
  const passwordBuffer = stringToArrayBuffer(password);

  const baseKey = await crypto.subtle.importKey('raw', masterKey, 'PBKDF2', false, ['deriveBits']);

  const hash = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: passwordBuffer,
      iterations: 1,
      hash: 'SHA-256',
    },
    baseKey,
    256,
  );

  return arrayBufferToBase64(hash);
}

/**
 * Stretch master key to get encryption and MAC keys
 * Uses HKDF-Expand with 'enc' and 'mac' info strings
 */
export async function stretchKey(masterKey: ArrayBuffer): Promise<SymmetricCryptoKey> {
  const encKeyBuffer = await hkdfExpand(masterKey, 'enc', 32);
  const macKeyBuffer = await hkdfExpand(masterKey, 'mac', 32);

  // Combine for the full key
  const fullKey = new Uint8Array(64);
  fullKey.set(new Uint8Array(encKeyBuffer), 0);
  fullKey.set(new Uint8Array(macKeyBuffer), 32);

  const encKey = await crypto.subtle.importKey('raw', encKeyBuffer, { name: 'AES-CBC' }, false, [
    'decrypt',
  ]);

  const macKey = await crypto.subtle.importKey(
    'raw',
    macKeyBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['verify'],
  );

  return {
    key: fullKey.buffer,
    encKey,
    macKey,
  };
}

/**
 * Parse a CipherString into its components
 * AES formats: Type.IV|CT|MAC or Type.IV|CT or just IV|CT|MAC (type 0)
 * RSA formats: Type.CT or Type.CT|MAC
 */
function parseCipherString(cipherString: string): ParsedCipherString {
  const parts = cipherString.split('.');

  // Handle formats: "2.IV|CT|MAC" or just "IV|CT|MAC" (type 0)
  let encType: EncryptionType;
  let dataPart: string;

  if (parts.length === 2) {
    encType = parseInt(parts[0], 10) as EncryptionType;
    dataPart = parts[1];
  } else {
    encType = EncryptionType.AesCbc256_B64;
    dataPart = parts[0];
  }

  const dataParts = dataPart.split('|');

  // RSA types (3, 4, 5, 6) have no IV - format is CT or CT|MAC
  const isRsaType =
    encType === EncryptionType.Rsa2048_OaepSha256_B64 ||
    encType === EncryptionType.Rsa2048_OaepSha1_B64 ||
    encType === EncryptionType.Rsa2048_OaepSha256_HmacSha256_B64 ||
    encType === EncryptionType.Rsa2048_OaepSha1_HmacSha256_B64;

  if (isRsaType) {
    // RSA format: CT or CT|MAC (no IV)
    const ct = base64ToUint8Array(dataParts[0]);
    const mac = dataParts.length > 1 ? base64ToUint8Array(dataParts[1]) : undefined;
    return { encType, iv: new Uint8Array(0), ct, mac };
  }

  // AES format: IV|CT or IV|CT|MAC
  if (dataParts.length < 2) {
    throw new Error('Invalid cipher string format');
  }

  const iv = base64ToUint8Array(dataParts[0]);
  const ct = base64ToUint8Array(dataParts[1]);
  const mac = dataParts.length > 2 ? base64ToUint8Array(dataParts[2]) : undefined;

  return { encType, iv, ct, mac };
}

/**
 * Verify HMAC-SHA256 MAC
 */
async function verifyMac(
  macKey: CryptoKey,
  iv: Uint8Array,
  ct: Uint8Array,
  expectedMac: Uint8Array,
): Promise<boolean> {
  // MAC is computed over IV + CT
  const data = new Uint8Array(iv.length + ct.length);
  data.set(iv, 0);
  data.set(ct, iv.length);

  return crypto.subtle.verify('HMAC', macKey, toArrayBuffer(expectedMac), toArrayBuffer(data));
}

/**
 * Decrypt a CipherString using the provided keys
 */
export async function decryptCipherString(
  cipherString: string | null | undefined,
  key: SymmetricCryptoKey,
): Promise<string> {
  if (!cipherString) {
    return '';
  }

  const parsed = parseCipherString(cipherString);

  // Verify MAC if present (required for type 2)
  if (parsed.encType === EncryptionType.AesCbc256_HmacSha256_B64) {
    if (!parsed.mac) {
      throw new Error('MAC required for this encryption type');
    }
    const macValid = await verifyMac(key.macKey, parsed.iv, parsed.ct, parsed.mac);
    if (!macValid) {
      throw new Error('MAC verification failed');
    }
  }

  // Decrypt using AES-CBC
  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-CBC',
      iv: toArrayBuffer(parsed.iv),
    },
    key.encKey,
    toArrayBuffer(parsed.ct),
  );

  return arrayBufferToString(decrypted);
}

/**
 * Decrypt the encrypted symmetric key returned from login
 * The Key field in login response is encrypted with the stretched master key
 */
export async function decryptEncryptedKey(
  encryptedKey: string,
  stretchedKey: SymmetricCryptoKey,
  opts?: { encryptable?: boolean },
): Promise<SymmetricCryptoKey> {
  const decryptedKeyBuffer = await decryptToBuffer(encryptedKey, stretchedKey);

  // The decrypted key is 64 bytes: 32 bytes enc key + 32 bytes mac key
  const encKeyBuffer = decryptedKeyBuffer.slice(0, 32);
  const macKeyBuffer = decryptedKeyBuffer.slice(32, 64);

  const encUsages: KeyUsage[] = opts?.encryptable ? ['encrypt', 'decrypt'] : ['decrypt'];
  const macUsages: KeyUsage[] = opts?.encryptable ? ['sign', 'verify'] : ['verify'];

  const encKey = await crypto.subtle.importKey('raw', encKeyBuffer, { name: 'AES-CBC' }, false, encUsages);

  const macKey = await crypto.subtle.importKey(
    'raw',
    macKeyBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    macUsages,
  );

  return {
    key: decryptedKeyBuffer,
    encKey,
    macKey,
  };
}

/**
 * Decrypt to raw ArrayBuffer (for key decryption)
 */
export async function decryptToBuffer(
  cipherString: string,
  key: SymmetricCryptoKey,
): Promise<ArrayBuffer> {
  if (!cipherString) {
    throw new Error('Empty cipher string');
  }

  const parsed = parseCipherString(cipherString);

  // Verify MAC if present
  if (
    parsed.mac &&
    (parsed.encType === EncryptionType.AesCbc256_HmacSha256_B64 ||
      parsed.encType === EncryptionType.AesCbc128_HmacSha256_B64)
  ) {
    const macValid = await verifyMac(key.macKey, parsed.iv, parsed.ct, parsed.mac);
    if (!macValid) {
      throw new Error('MAC verification failed');
    }
  }

  // Decrypt using AES-CBC
  return crypto.subtle.decrypt(
    {
      name: 'AES-CBC',
      iv: toArrayBuffer(parsed.iv),
    },
    key.encKey,
    toArrayBuffer(parsed.ct),
  );
}

/**
 * Decrypt organization key using private key
 * Organization keys are RSA encrypted
 */
export async function decryptOrgKey(
  encryptedOrgKey: string,
  privateKey: ArrayBuffer,
): Promise<SymmetricCryptoKey> {
  const parsed = parseCipherString(encryptedOrgKey);

  // Import private key for RSA decryption
  const rsaKey = await crypto.subtle.importKey(
    'pkcs8',
    privateKey,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-1', // Bitwarden uses SHA-1 for RSA-OAEP
    },
    false,
    ['decrypt'],
  );

  // The ciphertext is the RSA encrypted org key
  const decryptedKeyBuffer = await crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP',
    },
    rsaKey,
    toArrayBuffer(parsed.ct),
  );

  // The decrypted key is 64 bytes: 32 bytes enc key + 32 bytes mac key
  const encKeyBuffer = decryptedKeyBuffer.slice(0, 32);
  const macKeyBuffer = decryptedKeyBuffer.slice(32, 64);

  // Import keys for both encryption and decryption
  const encKey = await crypto.subtle.importKey('raw', encKeyBuffer, { name: 'AES-CBC' }, false, [
    'encrypt',
    'decrypt',
  ]);

  const macKey = await crypto.subtle.importKey(
    'raw',
    macKeyBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign',
    'verify'],
  );

  return {
    key: decryptedKeyBuffer,
    encKey,
    macKey,
  };
}

/**
 * Encrypt raw bytes to CipherString format (Type 2: AES-256-CBC + HMAC-SHA256)
 * Returns: "2.IV|CT|MAC"
 * Use this for binary data (e.g. Send secret key).
 * For string data, use encryptCipherString() which calls this internally.
 */
export async function encryptBuffer(
  data: ArrayBuffer,
  key: SymmetricCryptoKey,
): Promise<string> {
  // Generate random IV (16 bytes for AES-CBC)
  const iv = crypto.getRandomValues(new Uint8Array(16));

  // Encrypt using AES-CBC
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key.encKey,
    data,
  );
  const ctArray = new Uint8Array(ct);

  // Compute MAC over IV + CT
  const macData = new Uint8Array(iv.length + ctArray.length);
  macData.set(iv, 0);
  macData.set(ctArray, iv.length);

  const mac = await crypto.subtle.sign('HMAC', key.macKey, macData);

  // Format: "2.IV|CT|MAC" (all base64)
  return `2.${arrayBufferToBase64(iv.buffer)}|${arrayBufferToBase64(ct)}|${arrayBufferToBase64(mac)}`;
}

/**
 * Encrypt a string to CipherString format (Type 2: AES-256-CBC + HMAC-SHA256)
 * Returns: "2.IV|CT|MAC"
 */
export async function encryptCipherString(
  plaintext: string,
  key: SymmetricCryptoKey,
): Promise<string> {
  return encryptBuffer(stringToArrayBuffer(plaintext), key);
}

/**
 * Decrypt private key from login response
 */
export async function decryptPrivateKey(
  encryptedPrivateKey: string,
  userKey: SymmetricCryptoKey,
): Promise<ArrayBuffer> {
  return decryptToBuffer(encryptedPrivateKey, userKey);
}

// Decrypted custom field
export interface DecryptedField {
  type: FieldType;
  name: string;
  value: string;
}

// Decrypted card data
export interface DecryptedCard {
  cardholderName?: string;
  brand?: string;
  number?: string;
  expMonth?: string;
  expYear?: string;
  code?: string;
}

// Decrypted identity data
export interface DecryptedIdentity {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  username?: string;
  company?: string;
  ssn?: string;
  passportNumber?: string;
  licenseNumber?: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

// Type for decrypted vault item
export interface DecryptedVaultItem {
  id: string;
  type: CipherType;
  name: string;
  notes?: string;

  // Login (type=1)
  username?: string;
  password?: string;
  totp?: string;
  uris?: string[];

  // Card (type=3)
  card?: DecryptedCard;

  // Identity (type=4)
  identity?: DecryptedIdentity;

  // Custom fields (all types)
  fields?: DecryptedField[];

  collectionIds: string[];
}

/**
 * Decrypt a vault cipher item (supports all cipher types)
 */
export async function decryptCipher(
  cipher: VaultCipher,
  key: SymmetricCryptoKey,
): Promise<DecryptedVaultItem> {
  const name = await decryptCipherString(cipher.name, key);
  const notes = cipher.notes ? await decryptCipherString(cipher.notes, key) : undefined;

  const result: DecryptedVaultItem = {
    id: cipher.id,
    type: cipher.type,
    name,
    notes,
    collectionIds: cipher.collectionIds,
  };

  // Login (type=1)
  if (cipher.type === CipherType.Login && cipher.login) {
    result.username = cipher.login.username
      ? await decryptCipherString(cipher.login.username, key)
      : undefined;
    result.password = cipher.login.password
      ? await decryptCipherString(cipher.login.password, key)
      : undefined;
    result.totp = cipher.login.totp
      ? await decryptCipherString(cipher.login.totp, key)
      : undefined;

    if (cipher.login.uris && cipher.login.uris.length > 0) {
      result.uris = await Promise.all(
        cipher.login.uris.map((u) => decryptCipherString(u.uri, key))
      );
    }
  }

  // Card (type=3)
  if (cipher.type === CipherType.Card && cipher.card) {
    result.card = {
      cardholderName: cipher.card.cardholderName
        ? await decryptCipherString(cipher.card.cardholderName, key)
        : undefined,
      brand: cipher.card.brand
        ? await decryptCipherString(cipher.card.brand, key)
        : undefined,
      number: cipher.card.number
        ? await decryptCipherString(cipher.card.number, key)
        : undefined,
      expMonth: cipher.card.expMonth
        ? await decryptCipherString(cipher.card.expMonth, key)
        : undefined,
      expYear: cipher.card.expYear
        ? await decryptCipherString(cipher.card.expYear, key)
        : undefined,
      code: cipher.card.code
        ? await decryptCipherString(cipher.card.code, key)
        : undefined,
    };
  }

  // Identity (type=4)
  if (cipher.type === CipherType.Identity && cipher.identity) {
    result.identity = {
      title: cipher.identity.title
        ? await decryptCipherString(cipher.identity.title, key)
        : undefined,
      firstName: cipher.identity.firstName
        ? await decryptCipherString(cipher.identity.firstName, key)
        : undefined,
      middleName: cipher.identity.middleName
        ? await decryptCipherString(cipher.identity.middleName, key)
        : undefined,
      lastName: cipher.identity.lastName
        ? await decryptCipherString(cipher.identity.lastName, key)
        : undefined,
      username: cipher.identity.username
        ? await decryptCipherString(cipher.identity.username, key)
        : undefined,
      company: cipher.identity.company
        ? await decryptCipherString(cipher.identity.company, key)
        : undefined,
      ssn: cipher.identity.ssn
        ? await decryptCipherString(cipher.identity.ssn, key)
        : undefined,
      passportNumber: cipher.identity.passportNumber
        ? await decryptCipherString(cipher.identity.passportNumber, key)
        : undefined,
      licenseNumber: cipher.identity.licenseNumber
        ? await decryptCipherString(cipher.identity.licenseNumber, key)
        : undefined,
      email: cipher.identity.email
        ? await decryptCipherString(cipher.identity.email, key)
        : undefined,
      phone: cipher.identity.phone
        ? await decryptCipherString(cipher.identity.phone, key)
        : undefined,
      address1: cipher.identity.address1
        ? await decryptCipherString(cipher.identity.address1, key)
        : undefined,
      address2: cipher.identity.address2
        ? await decryptCipherString(cipher.identity.address2, key)
        : undefined,
      address3: cipher.identity.address3
        ? await decryptCipherString(cipher.identity.address3, key)
        : undefined,
      city: cipher.identity.city
        ? await decryptCipherString(cipher.identity.city, key)
        : undefined,
      state: cipher.identity.state
        ? await decryptCipherString(cipher.identity.state, key)
        : undefined,
      postalCode: cipher.identity.postalCode
        ? await decryptCipherString(cipher.identity.postalCode, key)
        : undefined,
      country: cipher.identity.country
        ? await decryptCipherString(cipher.identity.country, key)
        : undefined,
    };
  }

  // Custom Fields (all types)
  if (cipher.fields && cipher.fields.length > 0) {
    result.fields = await Promise.all(
      cipher.fields.map(async (f) => ({
        type: f.type,
        name: f.name ? await decryptCipherString(f.name, key) : '',
        value: f.value ? await decryptCipherString(f.value, key) : '',
      }))
    );
  }

  return result;
}

// ==================== Bitwarden Send ====================

/**
 * Generate a 128-bit (16 byte) random secret for Send
 */
export function generateSendSecret(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * Derive Send encryption key from secret using full HKDF (Extract + Expand)
 * NOT hkdfExpand() which is Expand-only.
 * Returns SymmetricCryptoKey with encrypt+sign usages.
 */
export async function deriveSendKey(secret: Uint8Array, opts?: { decryptable?: boolean }): Promise<SymmetricCryptoKey> {
  // Import raw secret as HKDF key material
  const importedSecret = await crypto.subtle.importKey(
    'raw', toArrayBuffer(secret), 'HKDF', false, ['deriveBits'],
  );

  // Full HKDF: Extract (with salt) + Expand (with info) → 512 bits = 64 bytes
  const keyBytes = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode('bitwarden-send'),
      info: new TextEncoder().encode('send'),
    },
    importedSecret,
    512,
  );

  // Split: 32 bytes enc + 32 bytes mac
  const encKeyBuffer = keyBytes.slice(0, 32);
  const macKeyBuffer = keyBytes.slice(32, 64);

  const encUsages: KeyUsage[] = opts?.decryptable ? ['encrypt', 'decrypt'] : ['encrypt'];
  const macUsages: KeyUsage[] = opts?.decryptable ? ['sign', 'verify'] : ['sign'];

  const encKey = await crypto.subtle.importKey(
    'raw', encKeyBuffer, { name: 'AES-CBC' }, false, encUsages,
  );
  const macKey = await crypto.subtle.importKey(
    'raw', macKeyBuffer, { name: 'HMAC', hash: 'SHA-256' }, false, macUsages,
  );

  return { key: keyBytes, encKey, macKey };
}

/**
 * Base64url encode (RFC 4648 §5): +→- /→_ trim =
 * Standard base64 is NOT valid for URL fragments.
 */
export function base64urlEncode(buffer: ArrayBuffer): string {
  return arrayBufferToBase64(buffer)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
